
import { useState, useRef } from 'react';
import { toast } from '@/hooks/use-toast';
import { readExcelFile } from '@/utils/excel/core/fileReader';
import { extractCategoriesFromSheet } from '@/utils/excel/parsers/categoryParser';
import { useBulkCreateCategories } from '@/hooks/use-category-management';

export const useCategoryUpload = (onUploadComplete: () => void) => {
  const [file, setFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<string[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const bulkCreateCategories = useBulkCreateCategories();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setIsProcessing(true);
    
    try {
      const workbook = await readExcelFile(selectedFile);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      
      // Extract categories using the updated function
      const categories = extractCategoriesFromSheet(worksheet);
      
      if (categories.length === 0) {
        toast({
          title: 'No categories found',
          description: 'Please ensure your Excel file has a column named "name" containing category names.',
          variant: 'destructive',
        });
      } else {
        setPreviewData(categories);
        toast({
          title: 'File processed',
          description: `Found ${categories.length} categories ready to upload.`,
        });
      }
    } catch (error) {
      console.error('Error processing file:', error);
      toast({
        title: 'Error',
        description: 'Failed to process the Excel file. Please check the format.',
        variant: 'destructive',
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpload = async () => {
    if (previewData.length === 0) return;
    
    try {
      const result = await bulkCreateCategories.mutateAsync({ names: previewData });
      
      // If there were duplicates, inform the user but still consider it a success
      if (result.skipped > 0 && result.added === 0) {
        toast({
          title: 'No new categories added',
          description: `All ${result.skipped} categories already exist in the database.`,
          variant: 'default',
        });
      } else if (result.skipped > 0) {
        toast({
          title: 'Categories Partially Uploaded',
          description: `Added ${result.added} new categories. Skipped ${result.skipped} existing categories.`,
        });
      } else {
        toast({
          title: 'Categories Uploaded',
          description: `Successfully added ${result.added} categories.`,
        });
      }
      
      // Reset the form
      handleReset();
      
      // Notify parent component
      onUploadComplete();
    } catch (error) {
      console.error('Failed to upload categories:', error);
      toast({
        title: 'Upload Failed',
        description: 'Failed to upload categories. Please try again later.',
        variant: 'destructive',
      });
    }
  };

  const handleReset = () => {
    setFile(null);
    setPreviewData([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return {
    file,
    previewData,
    isProcessing,
    fileInputRef,
    bulkCreateCategories,
    handleFileChange,
    handleUpload,
    handleReset
  };
};
